'use client'

import { useCurrentUser } from '@/hooks/use-current-user'
import { syncUserPreferencesAction } from '@/lib/services/actions/user-preferences'
import {
  clearLocalPreferences,
  extractUIPreferences,
  hasLocalPreferencesToSync,
  loadLocalPreferences
} from '@/lib/services/storage/user-preferences'
import { updateCookiePreferences } from '@/lib/store/core/privacy'
import { initialState, updateSettings } from '@/lib/store/core/settings'
import { ActionResponse } from '@/lib/types/core/response'
import { UserSettingsItem } from '@/lib/types/user/settings'
import { showToast } from '@/lib/utils/toast'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

async function fetchUserSettings(): Promise<ActionResponse<UserSettingsItem>> {
  const res = await fetch('/api/user/settings')
  return res.json()
}

/**
 * UserSettingsLoader component
 *
 * This component is responsible for:
 * 1. Loading user preferences from the appropriate source (server for authenticated users, localStorage for others)
 * 2. Initializing Redux store with these preferences
 * 3. Syncing local preferences to the server when a user logs in
 * 4. Keeping cookie preferences in sync with notification settings
 */
export function UserSettingsLoader() {
  const dispatch = useDispatch()
  const user = useCurrentUser()
  const [initialized, setInitialized] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [lastUserId, setLastUserId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function loadPreferences() {
      setIsLoading(true)
      setError(null)

      try {
        // If user logged in and their ID changed from last time (login event)
        const isNewLogin: boolean = Boolean(user?.id && user.id !== lastUserId)

        if (isNewLogin) {
          // User just logged in - we should sync local preferences to the server
          if (hasLocalPreferencesToSync()) {
            const localPreferences = loadLocalPreferences()

            try {
              // Attempt to sync local preferences with server
              const syncResponse = await syncUserPreferencesAction(localPreferences)

              if (syncResponse.success) {
                // Clear local preferences after successful sync
                clearLocalPreferences()
                console.log('Successfully synced local preferences to server')
              } else {
                console.error('Failed to sync preferences:', syncResponse.error)
                setError(`Failed to sync preferences: ${syncResponse.error}`)
              }
            } catch (syncError) {
              console.error('Failed to sync preferences:', syncError)
              setError('Failed to sync preferences due to a network error')
              // Continue to load preferences from server even if sync fails
            }
          }
        }

        if (user?.id) {
          // User is logged in, load preferences from the database
          const response = await fetchUserSettings()

          if (response.success && response.data) {
            try {
              // Extract UI preferences from metaData
              const uiPreferences = extractUIPreferences(response.data)

              // Combine regular preferences with UI preferences
              const combinedPreferences = {
                ...response.data,
                ...uiPreferences
              }

              // Update global settings with server data
              dispatch(updateSettings(combinedPreferences))

              // Update cookie preferences based on notification settings
              dispatch(
                updateCookiePreferences({
                  categories: {
                    essential: true, // Always true
                    functional: response.data.emailNotifications || response.data.pushNotifications,
                    analytics: response.data.emailNotifications, // Use email notifications as proxy for analytics
                    advertising: response.data.marketingEmails,
                    social: response.data.pushNotifications // Use push notifications as proxy for social
                  },
                  customized: true
                })
              )
            } catch (error) {
              console.error('Error processing user preferences:', error)
              setError('Error processing user preferences')

              // Still update settings with what we have
              dispatch(updateSettings(response.data))
            }
          } else {
            setError(response.error || 'Failed to load user settings')
            console.error('Failed to load user settings:', response.error)

            // Load default preferences if server fetch failed
            dispatch(updateSettings(initialState))
          }
        } else {
          // User is not logged in, load preferences from localStorage
          const localPreferences = loadLocalPreferences()

          if (Object.keys(localPreferences).length > 0) {
            // Combine with defaults to ensure all fields have values
            const combinedPreferences = {
              ...initialState,
              ...localPreferences
            }

            dispatch(updateSettings(combinedPreferences))

            // If notification settings exist in localStorage, initialize cookie preferences
            const { emailNotifications, pushNotifications, marketingEmails } = combinedPreferences

            dispatch(
              updateCookiePreferences({
                categories: {
                  essential: true, // Always true
                  functional: Boolean(emailNotifications) || Boolean(pushNotifications),
                  analytics: Boolean(emailNotifications),
                  advertising: Boolean(marketingEmails),
                  social: Boolean(pushNotifications)
                },
                customized: true
              })
            )
          } else {
            // No local preferences, use defaults
            dispatch(updateSettings(initialState))
          }
        }

        // Update lastUserId to track login state changes
        setLastUserId(user?.id || null)
        setInitialized(true)
      } catch (error) {
        console.error('Failed to load preferences:', error)
        setError('Failed to load preferences')

        // Load defaults in case of error
        dispatch(updateSettings(initialState))
      } finally {
        setIsLoading(false)
      }
    }

    // Always load preferences when user status changes or component mounts
    if (!initialized || user?.id !== lastUserId) {
      loadPreferences()
    }
  }, [dispatch, user, initialized, lastUserId])

  // Show error toast if something went wrong during loading
  useEffect(() => {
    if (error) {
      showToast({
        title: error,
        color: 'danger',
        timeout: 5000
      })
    }
  }, [error])

  // This is a utility component, it doesn't render anything
  return null
}
